import React, { useEffect } from 'react'
import styled from 'styled-components'

const Wrap = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`

const Table = styled.table`
  width: 100%;
  background: #fff;
  border-collapse: collapse;
  margin: 12px 0 8px;

  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 8px;
    text-align: center;
    vertical-align: middle;
  }

  th {
    background: #f8fafc;
    font-size: 12px;
    font-weight: 600;
  }

  th .slot {
    display: block;
    color: #111827;
  }

  th .class-name {
    display: block;
    margin-top: 4px;
    font-weight: 400;
    color: #6b7280;
    font-size: 11px;
  }

  td.row-label,
  th.row-label {
    text-align: left;
    font-weight: 600;
    white-space: nowrap;
  }

  td.error {
    background: #fef2f2;
  }
`

const Preview = styled.p`
  color: #4b5563;
  font-size: 13px;
  margin-bottom: 4px;
`

export const SLOTS = [1, 2, 3, 4, 5, 6, 7]
const CADENCES = [1, 2, 3, 4, 5, 6, 7]
const QUIZ_DAYS = 5

export const defaultCadenceMaps = {
  1: [1],
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 3, 4, 5],
  6: [1, 2, 3, 4, 5, 6],
  7: [1, 2, 3, 4, 5, 6, 7]
}

const sameDays = (left, right) => left.join() === right.join()

export const repairCadenceMaps = (items, cadenceMaps = {}) => {
  const filled = items
    .map((item, index) => (item?.id ? index + 1 : null))
    .filter(Boolean)

  return CADENCES.reduce((acc, cadence) => {
    const raw = (cadenceMaps[cadence] || cadenceMaps[String(cadence)] || []).map(Number)
    const kept = raw.filter((day) => (filled.length === 0 ? day <= QUIZ_DAYS : filled.includes(day)))
    const hidADay = raw.some((day) => !kept.includes(day))
    if (cadence > QUIZ_DAYS && !filled.includes(cadence)) {
      acc[cadence] = []
      return acc
    }
    if (cadence > QUIZ_DAYS && filled.includes(cadence) && kept.length === 0) {
      const starter = defaultCadenceMaps[cadence].filter((day) => filled.includes(day))
      acc[cadence] = starter.length === cadence ? starter : []
      return acc
    }
    if (!hidADay || kept.length === cadence) {
      acc[cadence] = kept
      return acc
    }
    const fallback = defaultCadenceMaps[cadence].filter((day) =>
      filled.length === 0 ? day <= QUIZ_DAYS : filled.includes(day)
    )
    if (fallback.length === cadence) {
      acc[cadence] = fallback
      return acc
    }
    acc[cadence] = cadence <= QUIZ_DAYS ? defaultCadenceMaps[cadence] : []
    return acc
  }, {})
}

const CadenceMatrix = ({
  weekLabel,
  classLabels = [],
  slotFilled = [],
  cadenceMaps,
  fieldPrefix,
  setFieldValue
}) => {
  const nameFor = (slot) => classLabels[slot - 1]
  const hasClass = (slot) => Boolean(slotFilled[slot - 1])
  const filledSlots = SLOTS.filter((slot) => hasClass(slot))
  const visibleSlots = SLOTS.filter((slot) => slot <= QUIZ_DAYS || hasClass(slot))
  const visibleCadences = CADENCES.filter(
    (cadence) => cadence <= QUIZ_DAYS || hasClass(cadence)
  )
  const filledKey = filledSlots.join()
  const repaired = repairCadenceMaps(
    SLOTS.map((slot) => ({ id: hasClass(slot) ? slot : '' })),
    cadenceMaps
  )

  useEffect(() => {
    CADENCES.forEach((cadence) => {
      const current = (cadenceMaps[cadence] || []).map(Number)
      const next = repaired[cadence] || []
      if (!sameDays(current, next)) {
        setFieldValue(`${fieldPrefix}.${cadence}`, next)
      }
    })
  }, [filledKey])

  const toggle = (cadence, slot) => {
    const current = (repaired[cadence] || []).map(Number)
    if (current.includes(slot)) {
      setFieldValue(
        `${fieldPrefix}.${cadence}`,
        current.filter((value) => value !== slot)
      )
      return
    }
    if (current.length >= cadence) return
    setFieldValue(`${fieldPrefix}.${cadence}`, [...current, slot].sort((a, b) => a - b))
  }

  return (
    <Wrap>
      <h4>Workouts vs rest for this week</h4>
      <p className='text-muted'>
        Columns are the class slots above, not calendar days. Classes 1–5 are
        required. When class 6 is added, the 6-day row is required. When class
        7 is added, the 7-day row is required. The quiz asks for 1 to 5 days a
        week. Check which classes are workouts for that schedule. The unchecked
        slots are rest days — only for {weekLabel}.
      </p>
      <Table>
        <thead>
          <tr>
            <th className='row-label'>If they train</th>
            {visibleSlots.map((slot) => (
              <th key={slot}>
                <span className='slot'>Class {slot}</span>
                <span className='class-name'>
                  {classLabels[slot - 1] || 'Empty slot'}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleCadences.map((cadence) => {
            const selected = repaired[cadence] || []
            const checkedEmpty = selected.some((slot) => !hasClass(slot))
            const countMismatch = selected.length !== cadence
            const invalid =
              filledSlots.length === 0
                ? countMismatch
                : countMismatch || checkedEmpty
            return (
              <tr key={cadence}>
                <td className={`row-label ${invalid ? 'error' : ''}`}>
                  {cadence} day{cadence === 1 ? '' : 's'}
                  {invalid ? ` (${selected.length}/${cadence})` : ''}
                </td>
                {visibleSlots.map((slot) => {
                  const checked = selected.includes(slot)
                  const atMax = selected.length >= cadence
                  return (
                    <td key={slot} className={invalid ? 'error' : ''}>
                      <input
                        type='checkbox'
                        aria-label={`${weekLabel}, ${cadence} days, class ${slot}`}
                        checked={checked}
                        disabled={!checked && atMax}
                        onChange={() => toggle(cadence, slot)}
                      />
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </Table>
      {visibleCadences.map((cadence) => {
        const selected = repaired[cadence] || []
        const workouts = selected.filter((slot) => nameFor(slot)).map(nameFor)
        const rest = filledSlots
          .filter((slot) => !selected.includes(slot))
          .map(nameFor)
        return (
          <Preview key={cadence}>
            {cadence} day{cadence === 1 ? '' : 's'}: workouts{' '}
            {workouts.length ? workouts.join(', ') : '—'}
            {rest.length ? `; rest ${rest.join(', ')}` : ''}
          </Preview>
        )
      })}
    </Wrap>
  )
}

export default CadenceMatrix
